import { formatExactLocationLabel, resolveNearestIndianCity } from "../config/indianCities";
import { NEAR_ME_RADIUS_KM } from "../config/marketplaceFilterEngine";
import { getCurrentPosition, getLastGeolocationError } from "./geolocation";
import { reverseGeocodeAddress } from "./reverseGeocode";

/**
 * Capture browser GPS and return marketplace search coords + City, State label.
 * Uses exact GPS for search; reverse-geocode (or nearest catalog city) for the filter label.
 * @param {{ fallback?: { latitude: string|number, longitude: string|number, locationLabel?: string } }} [opts]
 */
export async function captureNearMeMarketplaceLocation(opts = {}) {
  const fallback = opts?.fallback || null;
  const pos = await getCurrentPosition({
    enableHighAccuracy: true,
    timeout: 15000,
    maximumAge: 60000,
  });

  if (!pos || pos.latitude == null || pos.longitude == null) {
    const fbLat = Number(fallback?.latitude);
    const fbLng = Number(fallback?.longitude);
    if (Number.isFinite(fbLat) && Number.isFinite(fbLng)) {
      return {
        ok: true,
        coords: {
          latitude: String(fbLat),
          longitude: String(fbLng),
        },
        locationLabel: String(fallback?.locationLabel || "").trim() || "Your area",
        radiusKm: NEAR_ME_RADIUS_KM,
        usedFallback: true,
        fallbackReason: getLastGeolocationError()?.message || "GPS unavailable",
      };
    }
    return {
      ok: false,
      error:
        getLastGeolocationError()?.message ||
        "Unable to get your location. Please enable GPS/location access and try again.",
    };
  }

  const geo = await reverseGeocodeAddress({
    latitude: pos.latitude,
    longitude: pos.longitude,
  });

  // Prefer exact reverse-geocode locality; catalog nearest is fallback only.
  let locationLabel = formatExactLocationLabel({
    locality: geo?.locality,
    city: geo?.city,
    state: geo?.state,
  });

  if (!locationLabel) {
    const nearest = resolveNearestIndianCity(pos.latitude, pos.longitude);
    locationLabel = nearest?.label || "Your area";
  }

  return {
    ok: true,
    coords: {
      latitude: String(pos.latitude),
      longitude: String(pos.longitude),
    },
    locationLabel,
    radiusKm: NEAR_ME_RADIUS_KM,
  };
}
